import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, memoryLocalCache, getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { getAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: "AIzaSyDVvJmW-sByOyAqp-QBPnTTcNBa3S1aqVQ",
  authDomain: "group-marketing-hub.firebaseapp.com",
  projectId: "group-marketing-hub",
  storageBucket: "group-marketing-hub.firebasestorage.app",
  messagingSenderId: "468812440503",
  appId: "1:468812440503:web:b3c0ed59cbebf284797e32"
};

// Idempotent — if another app's bundle (e.g. voip) has already initialised
// the default Firebase app for this project, reuse it rather than throwing.
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth = getAuth(app);

// In-memory cache only — NO on-disk IndexedDB persistence. The persistent
// cache (persistentLocalCache) was a liability: when the contact collection
// briefly ballooned (voip sync), Firestore tried to persist tens of thousands
// of docs to IndexedDB and the on-disk store got bloated/corrupted, which then
// froze the whole app (Settings mounting new listeners tripped the broken
// persistence layer → every read returned empty). A hard refresh didn't help
// because the poisoned IndexedDB survives reloads. memoryLocalCache sidesteps
// that entire failure class — reads go to the server, nothing is written to
// disk, and any previously-poisoned IndexedDB is simply ignored. Fine for an
// internal admin tool with bounded data; no offline support needed.
let _db;
try {
  _db = initializeFirestore(app, { localCache: memoryLocalCache() });
} catch (e) {
  // Firestore may already be initialised by a sibling bundle — reuse it.
  try {
    _db = getFirestore(app);
  } catch {
    console.warn('[Firebase] Firestore init fell back to default:', e.message);
    _db = getFirestore(app);
  }
}
export const db = _db;

export const functions = getFunctions(app, 'europe-west2');
export const storage = getStorage(app);

// Firebase Analytics (GA4) — production-only so local dev sessions don't
// pollute dashboards. No-op if the browser doesn't support it.
if (import.meta.env.PROD) {
  isAnalyticsSupported().then((supported) => {
    if (!supported) return;
    try {
      getAnalytics(app);
    } catch (err) {
      console.warn('[Firebase] Analytics init failed:', err?.message || err);
    }
  }).catch(() => { /* isSupported rejected — ignore */ });
}

/**
 * Derive display name from Firebase user.
 * Uses displayName if set, otherwise converts email prefix:
 * "arran.north@trovex.com" → "Arran North"
 */
export function getUserDisplayName(user) {
  if (!user) return '';
  if (user.displayName) return user.displayName;
  const email = user.email || '';
  const prefix = email.split('@')[0] || '';
  return prefix
    .split(/[._-]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}
